// Tenancy scope options (BD-565/629).
//
// The reports-b Tenancy filter is a scope dimension exactly like
// Department / Location: a multi-select whose ticked values narrow the
// dashboard to a set of CHILD tenancies. Unlike the other dimensions its
// values are numeric tenancy ids (the backend accepts scope.tenancy as
// number[]), so the ids have to stay Numbers end to end.
//
// No new API: the tenancy list endpoint already returns every tenancy the
// caller can see (each carrying parentTenancyId), so the current tenancy's
// descendant subtree is derived client-side with buildTenancyTree - the same
// walk the tenancy Family Tree page uses. The current tenancy itself is
// excluded; only its descendants are offered as filter values.
//
// Integration hook: the descendant option list is reused by the shell filter
// chip (index.js) and the export builder, so both derive from one place.
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetTenanciesQuery } from 'store/services/tenancy.service';
import { buildTenancyTree } from '../tenancy/tenancyTree';

// Matches the family-tree page: the endpoint pages by tenancy id, so the page
// has to be big enough to reach the descendants being listed.
const TENANCY_PAGE_SIZE = 5000;

export const MAX_DEFAULT_POOLED_DESCENDANTS = 200;

export function defaultTenancyIds(tenancyOptions, currentTenancyId) {
    if (!tenancyOptions.length) return [];
    if (tenancyOptions.length - 1 > MAX_DEFAULT_POOLED_DESCENDANTS) return [currentTenancyId];
    return tenancyOptions.map((o) => o.value);
}

// Turn (getTenancies result + active tenancy id) into { label, value }[] for
// every DESCENDANT of the active tenancy, sorted by name. value is the numeric
// tenancy id. Returns [] when the tenancy has no children.
export function descendantTenancyOptions(tenancies, currentTenancyId) {
    const currentId = parseInt(currentTenancyId, 10);
    const { tenancyMap } = buildTenancyTree(tenancies, currentId);
    const nodes = [...tenancyMap.values()];
    const descendants = nodes
        .filter((node) => node.id !== currentId)
        .map((node) => ({ label: node.name, value: node.id }))
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    // Leaf tenancy: no children => no tenancy filter at all.
    if (!descendants.length) return [];
    // The viewed tenancy itself heads the list so it can be isolated. Leaving the
    // filter EMPTY means "all tenancies" (self + descendants), which the BFF resolves
    // server-side - matching how the other dimension filters treat empty as "all".
    const self = nodes.find((node) => node.id === currentId);
    const selfOption = { label: self ? `${self.name} (this tenancy)` : 'This tenancy', value: currentId };
    return [selfOption, ...descendants];
}

// Hook wrapper: fetches the tenancy list (cached by RTK Query) and returns the
// descendant options for the active tenancy. Empty array => no child tenancies
// => callers should not render the tenancy chip.
export function useDescendantTenancyOptions() {
    const currentTenancyId = useSelector((state) => state.tenancy.tenancyId);
    const { data: tenancies } = useGetTenanciesQuery({
        perPage: TENANCY_PAGE_SIZE,
        page: 0,
        includes: '',
        searchTerm: '',
        additionalQueryParams: '',
    });
    return useMemo(
        () => descendantTenancyOptions(tenancies, currentTenancyId),
        [tenancies, currentTenancyId],
    );
}
